import { Drawer } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ALERT_LIFECYCLE_UNRESOLVED } from '../../../constants.ts';
import { Alert, ServiceAnomaly } from '../../../schema/Alert.ts';
import { useAlerts } from '../../../services/Alerts.ts';
import { useTerritoryConsolidatedGeography } from '../../../services/Geography.ts';
import { useTerritory } from '../../../services/Territory.ts';
import { useVehiclesShiftByTerritory } from '../../../services/Vehicle.ts';
import { getServiceHealth } from '../../../utils/alertUtils.ts';
import { NotificationBadge } from '../../ui/Badge.tsx';
import { TextButton } from '../../ui/Button.tsx';
import { AlertCard } from './AlertCard.tsx';
import { ServiceHealth } from './ServiceHealth.tsx';

export function AlertDrawer() {
  const { t } = useTranslation();
  const params = useParams() as { territorySlug: string };
  const [showAlertDrawer, setShowAlertDrawer] = useState(false);
  const { data: alertData } = useAlerts(params.territorySlug, { lifecycle: ALERT_LIFECYCLE_UNRESOLVED });
  const { data: territory } = useTerritory(params.territorySlug);
  const { data: vehicles } = useVehiclesShiftByTerritory(params.territorySlug);
  const { data: geography } = useTerritoryConsolidatedGeography(params.territorySlug);
  const [serviceAnomalies, setServiceAnomalies] = useState<ServiceAnomaly[]>([]);
  const [showIssues, setShowIssues] = useState<boolean>(true);
  useEffect(() => {
    if (territory && vehicles && geography) {
      setServiceAnomalies(getServiceHealth(territory, vehicles.data, geography));
    }
  }, [territory, vehicles, alertData, geography]);

  return (
    <>
      <NotificationBadge
        onClick={() => setShowAlertDrawer(true)}
        count={(alertData?.length ?? 0) + serviceAnomalies.length}
      />
      <Drawer
        open={showAlertDrawer}
        placement={'right'}
        onClose={() => setShowAlertDrawer(false)}
        closeIcon={false}
        zIndex={2001}
        styles={{
          header: { border: 'none' },
          wrapper: {
            boxShadow: 'none',
            marginRight: '12px',
          },
          content: {
            borderRadius: '20px',
            marginTop: '72px',
            backgroundColor: 'rgb(115,119,145, 0.9)',
            height: 'calc(100vh - 60px - 50px - 24px)',
          },
          body: {
            padding: '12px',
          },
          mask: { opacity: 0 },
        }}>
        <ServiceHealth serviceAnomalies={serviceAnomalies} />
        <div className={'row mt-3 mb-2'}>
          <div className={'col'}>
            <p className={'card-subtitle'}>{t('alert.title-issues')}</p>
          </div>
          {(alertData?.length || 0) > 0 && (
            <div className={'col text-end'}>
              <TextButton
                text={`(${showIssues ? t('alert.hide') : t('alert.show')})`}
                onClick={() => setShowIssues(!showIssues)}
              />
            </div>
          )}
        </div>
        {(alertData?.length || 0) > 0 ? (
          <>
            {showIssues && (
              <>
                {alertData?.map((alert: Alert) => (
                  <AlertCard alert={alert} key={alert.id} />
                ))}
              </>
            )}
          </>
        ) : (
          <p className={'card-text text-start'}>
            <img
              src={'/theme/media/success-check.svg'}
              width="30"
              alt="Success icon"
              className={'me-2'}
            />
            {t('alert.no-alert')}
          </p>
        )}
      </Drawer>
    </>
  );
}
